import type {
  ApprovePurchaseOrderRequest,
  CancelPurchaseOrderRequest,
  CreatePurchaseOrderRequest,
  CreatePurchaseRequisitionRequest,
  CreateSupplierProfileRequest,
  CreateSupplierProfileResponse,
  CreateSupplierRfqRequest,
  ProcurementOverviewResponse,
  PurchaseOrderCommandResponse,
  PurchaseRequisitionCommandResponse,
  ReceivePurchaseOrderRequest,
  ReceivePurchaseOrderResponse,
  RecordSupplierInvoiceRequest,
  RecordSupplierInvoiceResponse,
  RecordSupplierQuoteRequest,
  RecordSupplierQuoteResponse,
  SupplierRfqCommandResponse
} from '@fmcgbyalex/contracts/procurement';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from 'react';
import { ApiError, FmcgApiClient, createIdempotencyKey } from '../lib/api-client';
import { useApplication } from './ApplicationProvider';

export type ProcurementNotice = Readonly<{
  tone: 'success' | 'error' | 'info';
  title: string;
  detail: string;
}>;

export type ProcurementContextValue = Readonly<{
  overview: ProcurementOverviewResponse | null;
  loading: boolean;
  refreshing: boolean;
  busyAction: string | null;
  error: ApiError | null;
  notice: ProcurementNotice | null;
  refresh: () => Promise<void>;
  dismissNotice: () => void;
  createSupplierProfile: (input: CreateSupplierProfileRequest) => Promise<CreateSupplierProfileResponse>;
  createRequisition: (input: CreatePurchaseRequisitionRequest) => Promise<PurchaseRequisitionCommandResponse>;
  createRfq: (input: CreateSupplierRfqRequest) => Promise<SupplierRfqCommandResponse>;
  recordSupplierQuote: (rfqId: string, input: RecordSupplierQuoteRequest) => Promise<RecordSupplierQuoteResponse>;
  createPurchaseOrder: (input: CreatePurchaseOrderRequest) => Promise<PurchaseOrderCommandResponse>;
  submitPurchaseOrder: (purchaseOrderId: string) => Promise<PurchaseOrderCommandResponse>;
  approvePurchaseOrder: (purchaseOrderId: string, input: ApprovePurchaseOrderRequest) => Promise<PurchaseOrderCommandResponse>;
  receivePurchaseOrder: (purchaseOrderId: string, input: ReceivePurchaseOrderRequest) => Promise<ReceivePurchaseOrderResponse>;
  cancelPurchaseOrder: (purchaseOrderId: string, input: CancelPurchaseOrderRequest) => Promise<PurchaseOrderCommandResponse>;
  recordSupplierInvoice: (purchaseOrderId: string, input: RecordSupplierInvoiceRequest) => Promise<RecordSupplierInvoiceResponse>;
}>;

const ProcurementContext = createContext<ProcurementContextValue | null>(null);

export function ProcurementProvider({ children }: PropsWithChildren) {
  const application = useApplication();
  const [overview, setOverview] = useState<ProcurementOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<ProcurementNotice | null>(null);
  const generation = useRef(0);

  const client = useMemo(
    () => application.authentication
      ? new FmcgApiClient(application.runtime, application.authentication)
      : null,
    [application.authentication, application.runtime]
  );

  useEffect(() => {
    if (
      application.status !== 'ready' ||
      !application.tenantId ||
      !application.session ||
      !client
    ) {
      ++generation.current;
      setOverview(null);
      setLoading(false);
      setError(null);
      return;
    }
    void loadProcurement(client, application.tenantId, true);
  }, [application.status, application.tenantId, application.session, client]);

  async function loadProcurement(activeClient: FmcgApiClient, tenantId: string, initial: boolean): Promise<void> {
    const currentGeneration = ++generation.current;
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError(null);

    const moduleEnabled = application.session?.modules.some(
      (module) => module.key === 'procurement' && module.enabled
    ) ?? false;
    const permissions = new Set(application.session?.permissions ?? []);
    const readable = moduleEnabled && [
      'procurement.suppliers.read',
      'procurement.requisitions.read',
      'procurement.rfq.read',
      'procurement.purchase-orders.read',
      'procurement.invoices.read'
    ].every((permission) => permissions.has(permission));

    try {
      const nextOverview = readable
        ? await activeClient.get<ProcurementOverviewResponse>('/v1/procurement/overview', tenantId)
        : null;
      if (currentGeneration !== generation.current) return;
      setOverview(nextOverview);
    } catch (caught) {
      if (currentGeneration !== generation.current) return;
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({
        tone: 'error',
        title: apiError.problem.title,
        detail: apiError.problem.detail ?? 'Procurement data could not be loaded.'
      });
    } finally {
      if (currentGeneration === generation.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }

  async function refresh(): Promise<void> {
    const activeClient = requireClient();
    const tenantId = requireTenantId();
    await loadProcurement(activeClient, tenantId, false);
  }

  async function runMutation<T>(
    label: string,
    successTitle: string,
    successDetail: string,
    operation: (activeClient: FmcgApiClient, tenantId: string) => Promise<T>,
    refreshInventory = false
  ): Promise<T> {
    const activeClient = requireClient();
    const tenantId = requireTenantId();
    setBusyAction(label);
    setNotice(null);
    try {
      const response = await operation(activeClient, tenantId);
      await Promise.all([
        loadProcurement(activeClient, tenantId, false),
        refreshInventory ? application.refreshAll() : Promise.resolve()
      ]);
      setNotice({ tone: 'success', title: successTitle, detail: successDetail });
      return response;
    } catch (caught) {
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({
        tone: 'error',
        title: apiError.problem.title,
        detail: apiError.problem.detail ?? `Request failed${apiError.correlationId ? ` · ${apiError.correlationId}` : ''}`
      });
      throw apiError;
    } finally {
      setBusyAction(null);
    }
  }

  function requireClient(): FmcgApiClient {
    if (!client) throw new Error('Authentication is required.');
    return client;
  }

  function requireTenantId(): string {
    if (!application.tenantId) throw new Error('A tenant must be selected.');
    return application.tenantId;
  }

  const value: ProcurementContextValue = {
    overview,
    loading,
    refreshing,
    busyAction,
    error,
    notice,
    refresh,
    dismissNotice: () => setNotice(null),
    createSupplierProfile: (input) => runMutation(
      'Creating supplier profile',
      'Supplier profile created',
      'Purchasing terms and inbound tolerance are now active.',
      (activeClient, tenantId) => activeClient.mutate<CreateSupplierProfileResponse>(
        'POST', '/v1/procurement/suppliers', input, tenantId,
        createIdempotencyKey('procurement-supplier-create')
      )
    ),
    createRequisition: (input) => runMutation(
      'Creating requisition',
      'Purchase requisition submitted',
      'Internal demand is available for sourcing and RFQ planning.',
      (activeClient, tenantId) => activeClient.mutate<PurchaseRequisitionCommandResponse>(
        'POST', '/v1/procurement/requisitions', input, tenantId,
        createIdempotencyKey('procurement-requisition-create')
      )
    ),
    createRfq: (input) => runMutation(
      'Issuing supplier RFQ',
      'Supplier RFQ issued',
      'Invited suppliers and requested quantities are now tracked.',
      (activeClient, tenantId) => activeClient.mutate<SupplierRfqCommandResponse>(
        'POST', '/v1/procurement/rfqs', input, tenantId,
        createIdempotencyKey('procurement-rfq-create')
      )
    ),
    recordSupplierQuote: (rfqId, input) => runMutation(
      'Recording supplier quotation',
      'Supplier quotation recorded',
      'Comparable unit prices, lead time and taxes were recalculated by the server.',
      (activeClient, tenantId) => activeClient.mutate<RecordSupplierQuoteResponse>(
        'POST', `/v1/procurement/rfqs/${encodeURIComponent(rfqId)}/quotes`, input,
        tenantId, createIdempotencyKey('procurement-supplier-quote')
      )
    ),
    createPurchaseOrder: (input) => runMutation(
      'Creating purchase order',
      'Purchase order created',
      'The server-priced order is ready for submission and approval.',
      (activeClient, tenantId) => activeClient.mutate<PurchaseOrderCommandResponse>(
        'POST', '/v1/procurement/purchase-orders', input, tenantId,
        createIdempotencyKey('procurement-po-create')
      )
    ),
    submitPurchaseOrder: (purchaseOrderId) => runMutation(
      'Submitting purchase order',
      'Purchase order submitted',
      'The order is open or awaiting protected approval based on its value.',
      (activeClient, tenantId) => activeClient.mutate<PurchaseOrderCommandResponse>(
        'POST', `/v1/procurement/purchase-orders/${encodeURIComponent(purchaseOrderId)}/submit`, {},
        tenantId, createIdempotencyKey('procurement-po-submit')
      )
    ),
    approvePurchaseOrder: (purchaseOrderId, input) => runMutation(
      'Approving purchase order',
      'Purchase order approved',
      'The order is released for supplier fulfilment and inbound receiving.',
      (activeClient, tenantId) => activeClient.mutate<PurchaseOrderCommandResponse>(
        'POST', `/v1/procurement/purchase-orders/${encodeURIComponent(purchaseOrderId)}/approve`, input,
        tenantId, createIdempotencyKey('procurement-po-approve')
      )
    ),
    receivePurchaseOrder: (purchaseOrderId, input) => runMutation(
      'Receiving purchase order',
      'Purchase order receipt posted',
      'PO progress and append-only inventory movements were committed together.',
      (activeClient, tenantId) => activeClient.mutate<ReceivePurchaseOrderResponse>(
        'POST', `/v1/procurement/purchase-orders/${encodeURIComponent(purchaseOrderId)}/receipts`, input,
        tenantId, createIdempotencyKey('procurement-po-receive')
      ),
      true
    ),
    cancelPurchaseOrder: (purchaseOrderId, input) => runMutation(
      'Cancelling purchase order',
      'Purchase order cancelled',
      'Further inbound receipts are now blocked for this order.',
      (activeClient, tenantId) => activeClient.mutate<PurchaseOrderCommandResponse>(
        'POST', `/v1/procurement/purchase-orders/${encodeURIComponent(purchaseOrderId)}/cancel`, input,
        tenantId, createIdempotencyKey('procurement-po-cancel')
      )
    ),
    recordSupplierInvoice: (purchaseOrderId, input) => runMutation(
      'Recording supplier invoice',
      'Supplier invoice recorded',
      'Quantity and price variances were calculated against the order and receipts.',
      (activeClient, tenantId) => activeClient.mutate<RecordSupplierInvoiceResponse>(
        'POST', `/v1/procurement/purchase-orders/${encodeURIComponent(purchaseOrderId)}/invoices`, input,
        tenantId, createIdempotencyKey('procurement-invoice-record')
      )
    )
  };

  return <ProcurementContext.Provider value={value}>{children}</ProcurementContext.Provider>;
}

export function useProcurement(): ProcurementContextValue {
  const context = useContext(ProcurementContext);
  if (!context) throw new Error('useProcurement must be used within ProcurementProvider.');
  return context;
}

function toApiError(caught: unknown): ApiError {
  if (caught instanceof ApiError) return caught;
  return new ApiError(
    0,
    {
      type: 'https://fmcgbyalex.com/problems/client-error',
      title: 'The procurement request failed',
      status: 0,
      detail: caught instanceof Error ? caught.message : 'An unexpected client error occurred.'
    },
    null
  );
}
